import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

import { loadToken, loginHandoff, loginTelegram, setToken } from '@/api';
import { BOT_LOGIN_LINK } from '@/links';
import { IS_NATIVE, onLoginLink, openExternal, telegramInitData } from '@/platform';
import { identifyUser, reportError } from '@/sentry';

type AuthState =
  | { status: 'loading' }
  | { status: 'ready' }
  | { status: 'guest' }
  | { status: 'error'; message: string; retry: () => void };

const AuthContext = createContext<AuthState>({ status: 'loading' });

export const openBotLogin = () => openExternal(BOT_LOGIN_LINK);

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

export function useIsGuest(): boolean {
  return useAuth().status === 'guest';
}

const READY: AuthState = { status: 'ready' };

function signIn(token: string): void {
  setToken(token);
  identifyUser(token);
}

function readyOnce(prev: AuthState): AuthState {
  return prev.status === 'ready' ? prev : READY;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    setState({ status: 'loading' });
    setAttempt((n) => n + 1);
  }, []);

  const fail = useCallback(
    (e: unknown) =>
      setState({
        message: e instanceof Error ? e.message : 'Authentication failed.',
        status: 'error',
        retry,
      }),
    [retry],
  );

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const devJwt = import.meta.env.DEV ? import.meta.env.VITE_DEV_JWT : undefined;
      if (devJwt) {
        signIn(devJwt);
        if (!cancelled) setState(readyOnce);
        return;
      }

      const cachedToken = loadToken();
      if (cachedToken) identifyUser(cachedToken);
      if (IS_NATIVE) {
        setState(cachedToken ? readyOnce : { status: 'guest' });
        return;
      }
      if (cachedToken) setState(readyOnce);

      try {
        const initDataRaw = telegramInitData();
        if (!initDataRaw) throw new Error('No Telegram init data available.');
        const { access_token } = await loginTelegram(initDataRaw);
        signIn(access_token);
        if (!cancelled) setState(readyOnce);
      } catch (e) {
        if (!cachedToken) reportError(e, { stage: 'auth-bootstrap' });
        if (!cancelled && !cachedToken) fail(e);
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [attempt, fail]);

  useEffect(
    () =>
      onLoginLink((handoff) => {
        if (loadToken()) return;
        setState({ status: 'loading' });
        loginHandoff(handoff).then(
          ({ access_token }) => {
            signIn(access_token);
            setState({ status: 'ready' });
          },
          (e: unknown) => {
            reportError(e, { stage: 'auth-handoff' });
            fail(e);
          },
        );
      }),
    [fail],
  );

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}
